import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AlertService, DialogService, NotifierService, TelegramBotComponent, TelegramBotService1, User, UserService } from '@wm-libs/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmptyDataComponent } from '../../../../core/components/empty-data/empty-data.component';
import { DataTableComponent } from '../../../../core/components/data-table/data-table';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgClass, NgIf, NgFor, AsyncPipe } from '@angular/common';
import { environment } from '../../../../../../src/environments/environment';
import { PoolAccessTokenService } from '../../../../core/services/pool-access-token.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { AccessTokenListItem, AccessTokenPermission, ModifiedAccessTokenListItem } from '../../../../core/interfaces/access-token.interface';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    MatButtonModule,
    DataTableComponent,
    EmptyDataComponent,
    AsyncPipe,
    TranslateModule,
    TelegramBotComponent,
  ],
})
export class SettingsComponent implements OnInit {
  displayedColumns: string[] = ['label', 'token', 'permissions', 'status', 'actions'];

  modifiedAccessTokens: ModifiedAccessTokenListItem[] | null = null;
  accessTokensLoaded = false;
  permissionsList: AccessTokenPermission[] = ['statistics', 'workers', 'incomes', 'balance'];

  telegramBotUrl = environment.poolTelegramBotUrl;

  userData$!: User | null;
  constructor(
    private dialogService: DialogService,
    private accessTokenService: PoolAccessTokenService,
    private translateService: TranslateService,
    private alertService: AlertService,
    private userService: UserService,
    private telegramBotService: TelegramBotService1,
    private notifier: NotifierService
  ) {}

  ngOnInit(): void {
    this.notifier.notifications$.subscribe((result) => {
      if (result === 'POOL.FARM_SWITCHED') {
        this.notifier.clearNotifications();
        this.getAccessTokensList();
      }
    });

    this.userService.userData$.subscribe((res) => {
      this.userData$ = res;
    });

    this.getAccessTokensList();
  }

  getAccessTokensList() {
    this.accessTokensLoaded = false;

    this.accessTokenService.getAccessTokensList().subscribe((res) => {
      this.accessTokensLoaded = true;

      this.modifiedAccessTokens = res?.map((item: AccessTokenListItem) => ({
        label: item.label,
        token: `${environment.techPoolDomain}/accessToken/${item.token}`,
        status: item.enabled,
        id: item.id,
        permissions: item.permissions,
      }));
    });
  }

  openAddAccessTokenDialog() {
    const config = {
      title: 'WM_TECH_POOL.ACCESSTOKEN.DIALOG.CREATE.TITLE',
      app: 'pool',
      fields: [
        { name: 'accessTokenName', label: 'WM_TECH_POOL.ACCESSTOKEN.LABEL', type: 'input', validators: [Validators.required] },
        {
          type: 'select',
          label: 'WM_TECH_POOL.ACCESSTOKEN.PERMISSIONS',
          name: 'permissions',
          options: this.permissionsList.map((permission: AccessTokenPermission) => ({
            value: permission,
            label: 'WM_TEC_POOL.PERMISSIONS.' + permission.toUpperCase(),
          })),
          multiple: true,
          validators: [Validators.required],
        },
      ],
    };

    const dialogRef = this.dialogService.openPopup(config);

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.accessTokenName) {
        this.accessTokenService.createAccessToken(result.accessTokenName, [...result.permissions]).subscribe(() => {
          this.getAccessTokensList();
        });
      }
    });
  }

  handleToggleAccessTokenStatus(event: { event: MatSlideToggleChange; element: ModifiedAccessTokenListItem }) {
    const { event: toggleEvent, element } = event;
    const originalState = !toggleEvent.checked;
    if (originalState) {
      this.accessTokenService.editAccessToken(element.id, element.label, false, element.permissions).subscribe();
    } else {
      this.accessTokenService.editAccessToken(element.id, element.label, true, element.permissions).subscribe();
    }
  }

  handleRemoveAccessToken(id: number) {
    const config = {
      title: 'WM_TECH_POOL.ACCESSTOKEN.DIALOG.REMOVE.TITLE',
      app: 'pool',
    };

    const dialogRef = this.dialogService.openPopup(config);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.accessTokenService.deleteAccessToken(id).subscribe(() => {
          this.getAccessTokensList();
        });
      }
    });
  }

  handleEditAccessTokenPermissions(element: ModifiedAccessTokenListItem) {
    const config = {
      title: 'WM_TECH_POOL.ACCESSTOKEN.DIALOG.EDIT.TITLE',
      app: 'pool',
      fields: [
        { name: 'accessTokenName', label: 'WM_TECH_POOL.ACCESSTOKEN.LABEL', value: element.label, type: 'input', validators: [Validators.required] },
        {
          type: 'select',
          label: 'WM_TECH_POOL.ACCESSTOKEN.PERMISSIONS',
          name: 'permissions',
          value: element.permissions,
          options: this.permissionsList.map((permission: AccessTokenPermission) => ({
            value: permission,
            label: 'WM_TEC_POOL.PERMISSIONS.' + permission.toUpperCase(),
          })),
          multiple: true,
          validators: [Validators.required],
        },
      ],
    };

    const dialogRef = this.dialogService.openPopup(config);

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.accessTokenName && result.permissions.length) {
        this.accessTokenService.editAccessToken(element.id, result.accessTokenName, element.status, [...result.permissions]).subscribe(() => {
          this.getAccessTokensList();
        });
      }
    });
  }

  onTelegramBotVerified(code: string) {
    this.telegramBotService.verifyPoolTelegramBot(code).subscribe(() => {
      this.alertService.showAlert(this.translateService.instant('WM_TECH_POOL.SETTINGS.TELEGRAM_BOT.ALERT.CONNECTED'), 'success');

      if (this.userData$) {
        const updatedUserData = { ...this.userData$, telegramIdVerified: true };
        this.userService.setUserData(updatedUserData);
      }
    });
  }
}
